import React, { useState } from 'react';
import { useContent } from '../hooks/useContent';
import { resolveAssetUrl } from '../lib/api';

const InvestmentSolutions = () => {
  const { t } = useContent('investment');
  const DEFAULT_PHOTO = 'https://customer-assets-rejwkqb3.emergentagent.net/job_stone-temple-preview/artifacts/tx8nthlm_Screenshot%202026-10-05%20at%2011.29.18%E2%80%AFPM.webp';
  const photoUrl = resolveAssetUrl(t('hero.photo_url', '') || DEFAULT_PHOTO);
  return (
    <main className="pt-32 pb-24" data-testid="investment-solutions-page">
      {/* Hero — text left, square image right (matches advisory services layout) */}
      <section className="max-w-[1440px] mx-auto px-8 md:px-14 py-12 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="font-sans text-[11px] tracking-[0.35em] text-navy/60 mb-8">{t('hero.kicker', 'INVESTMENT SOLUTIONS')}</p>
            <h1 className="font-serif text-5xl md:text-7xl text-navy leading-[0.95]" data-testid="investment-heading">
              {t('hero.title_line1', 'Built the way we invest')}<br /><span className="italic">{t('hero.title_line2', 'our own capital.')}</span>
            </h1>
            <p className="font-serif text-xl md:text-2xl text-navy leading-snug mt-10 max-w-2xl">
              {t('hero.body', "STP organizes investments by what they do for a portfolio, not what they're labeled — growth or income across a liquidity spectrum. That framework produces six strategies across three liquidity tiers, plus tailored SMAs and single-asset SPVs for concentrated positions.")}
            </p>
          </div>
          <div className="lg:col-span-5 flex lg:justify-end">
            <div className="relative w-full max-w-md overflow-hidden bg-cream2" data-testid="investment-visual-box">
              <img
                src={photoUrl}
                alt="Stone Temple Partners"
                className="block w-full h-auto"
              />
            </div>
          </div>
        </div>

        {/* four principles */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mt-16 pt-12 border-t border-navy/15">
          {(t('principles') || []).map((p, i) => (
            <div key={i}>
              <h4 className="font-serif text-xl text-navy leading-tight">{p.title}</h4>
              <p className="font-sans text-sm text-navy/70 mt-3 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Combined Vehicles Grid — hover to reveal descriptions, no target stats/numbers */}
      <section className="py-16 md:py-24 border-t border-navy/10" data-testid="investment-vehicles">
        <div className="max-w-[1440px] mx-auto px-8 md:px-14">
          <p className="font-sans text-[11px] tracking-[0.35em] text-navy/60 mb-6">{t('vehicles.kicker')}</p>
          <h3 className="font-serif text-4xl md:text-5xl text-navy leading-tight max-w-4xl">
            {t('vehicles.title')}
          </h3>
          <p className="font-serif text-lg text-navy/70 mt-6 max-w-3xl leading-snug">
            {t('vehicles.hint')}
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="vehicle-grid">
            {(t('vehicles.items') || []).map((v, i) => (
              <VehicleCard key={i} vehicle={v} cta={t('vehicles.cta')} />
            ))}
          </div>
        </div>
      </section>

      {/* Access — kept */}
      <section className="py-16 md:py-24 border-t border-navy/10" data-testid="investment-access">
        <div className="max-w-[1440px] mx-auto px-8 md:px-14">
          <p className="font-sans text-[11px] tracking-[0.35em] text-navy/60 mb-6">{t('access.kicker')}</p>
          <h4 className="font-serif text-3xl md:text-4xl text-navy leading-tight">{t('access.title')}</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-10">
            {(t('access.items') || []).map((a, i) => (
              <div key={i} className="border-t border-navy/25 pt-6">
                <h5 className="font-serif text-2xl text-navy leading-tight">{a.title}</h5>
                <p className="font-sans text-sm text-navy/75 mt-4 leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>

          <p className="mt-14 text-xs text-navy/50 leading-relaxed max-w-4xl">
            {t('disclaimer')}
          </p>
        </div>
      </section>
    </main>
  );
};

const VehicleCard = ({ vehicle, cta }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      tabIndex={0}
      className={`relative bg-cream border p-8 min-h-[240px] transition-colors ${
        hovered ? 'border-navy' : 'border-navy/15'
      }`}
      data-testid={`vehicle-card-${(vehicle.name || '').toLowerCase().replace(/\s+/g, '-')}`}
    >
      <p className="font-sans text-[10px] tracking-[0.3em] text-navy/60">
        {(vehicle.tier || '').toUpperCase()} · {(vehicle.purpose || '').toUpperCase()}
      </p>
      <h5 className="font-serif text-2xl md:text-3xl text-navy leading-tight mt-4">{vehicle.name}</h5>
      <p className="font-sans text-[10px] tracking-[0.25em] text-navy/50 mt-3">{(vehicle.tag || '').toUpperCase()}</p>

      <div className={`overflow-hidden transition-all duration-500 ${hovered ? 'max-h-96 opacity-100 mt-5' : 'max-h-0 opacity-0'}`}>
        <p className="font-sans text-sm text-navy/80 leading-relaxed pt-4 border-t border-navy/15">
          {vehicle.description}
        </p>
      </div>

      {!hovered && (
        <p className="font-sans text-[10px] tracking-[0.3em] text-navy/50 mt-6">{cta}</p>
      )}
    </div>
  );
};

export default InvestmentSolutions;
